import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  Mail,
  ArrowLeft,
  Plus,
  Upload,
  Send,
  FileText,
  Inbox,
  SendHorizontal,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  Sparkles,
  AlertCircle,
  Search,
  Filter,
  Gavel
} from "lucide-react";

const CORRESPONDENCE_TYPES = [
  { value: "owner_letter", label: "Owner Letter" },
  { value: "violation_notice", label: "Notice Letter" },
  { value: "drc_letter", label: "DRC Letter" },
  { value: "fine_notice", label: "Fine Notice" },
  { value: "hearing_notice", label: "Hearing Notice" },
  { value: "general", label: "General" },
  { value: "response", label: "Response" },
  { value: "appeal", label: "Appeal" }
];

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-gray-500/10 text-gray-600", icon: <FileText className="w-3 h-3" /> },
  pending_approval: { label: "Pending Approval", color: "bg-yellow-500/10 text-yellow-600", icon: <Clock className="w-3 h-3" /> },
  approved: { label: "Approved", color: "bg-blue-500/10 text-blue-600", icon: <CheckCircle className="w-3 h-3" /> },
  sent: { label: "Sent", color: "bg-green-500/10 text-green-600", icon: <Send className="w-3 h-3" /> },
  delivered: { label: "Delivered", color: "bg-green-600/10 text-green-700", icon: <CheckCircle className="w-3 h-3" /> },
  failed: { label: "Failed", color: "bg-red-500/10 text-red-600", icon: <XCircle className="w-3 h-3" /> }
};

const CorrespondencePage = ({ embedded = false }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [correspondence, setCorrespondence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modals
  const [uploadOpen, setUploadOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedCorr, setSelectedCorr] = useState(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteCorr, setPromoteCorr] = useState(null);
  const [promoteForm, setPromoteForm] = useState({
    target_type: "owner",
    target_owner_id: "",
    target_property_id: "",
    target_role: "",
    category: "other"
  });
  
  // Form states
  const [uploadForm, setUploadForm] = useState({
    subject: "",
    correspondence_type: "",
    owner_id: "",
    property_id: "",
    file: null
  });
  const [composeForm, setComposeForm] = useState({
    correspondence_type: "",
    subject: "",
    content: "",
    to_name: "",
    to_email: "",
    owner_id: "",
    property_id: "",
    violation_id: "",
    drc_submission_id: ""
  });
  const [aiDrafting, setAiDrafting] = useState(false);
  
  // Reference data
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [violations, setViolations] = useState([]);
  const [drcSubmissions, setDrcSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, inbound: 0, outbound: 0, pending_action: 0, sent: 0 });

  const fetchCorrespondence = async () => {
    try {
      const params = new URLSearchParams();
      if (activeTab === "inbound") params.append("direction", "inbound");
      if (activeTab === "outbound") params.append("direction", "outbound");
      if (typeFilter && typeFilter !== "all") params.append("correspondence_type", typeFilter);
      
      const response = await axios.get(`${API}/correspondence?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrespondence(response.data);
    } catch (error) {
      toast.error("Failed to load correspondence");
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [ownersRes, propsRes, violationsRes, drcRes, statsRes] = await Promise.all([
        axios.get(`${API}/owners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/violations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/drc`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/correspondence/stats/summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      setOwners(ownersRes.data);
      setProperties(propsRes.data);
      setViolations(violationsRes.data);
      setDrcSubmissions(drcRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch reference data");
    }
  };

  useEffect(() => {
    fetchCorrespondence();
    fetchReferenceData();
  }, [token, activeTab, typeFilter]);

  const handleUploadInbound = async () => {
    if (!uploadForm.subject || !uploadForm.correspondence_type || !uploadForm.file) {
      toast.error("Please fill in required fields and select a file");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadForm.file);
    formData.append("subject", uploadForm.subject);
    formData.append("correspondence_type", uploadForm.correspondence_type);
    if (uploadForm.owner_id) formData.append("owner_id", uploadForm.owner_id);
    if (uploadForm.property_id) formData.append("property_id", uploadForm.property_id);

    try {
      const response = await axios.post(`${API}/correspondence/inbound/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success("Inbound correspondence uploaded and analyzed");
      
      if (response.data.summary) {
        toast.info(`AI Summary: ${response.data.summary.substring(0, 100)}...`);
      }
      
      setUploadOpen(false);
      setUploadForm({ subject: "", correspondence_type: "", owner_id: "", property_id: "", file: null });
      fetchCorrespondence();
      fetchReferenceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    }
  };

  const handleCreateOutbound = async () => {
    if (!composeForm.correspondence_type || !composeForm.subject || !composeForm.content) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/correspondence`, {
        direction: "outbound",
        correspondence_type: composeForm.correspondence_type,
        subject: composeForm.subject,
        content: composeForm.content,
        to_name: composeForm.to_name,
        to_email: composeForm.to_email,
        owner_id: composeForm.owner_id || null,
        property_id: composeForm.property_id || null,
        violation_id: composeForm.violation_id || null,
        drc_submission_id: composeForm.drc_submission_id || null,
        delivery_method: "email"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Correspondence created as draft");
      setComposeOpen(false);
      setComposeForm({
        correspondence_type: "",
        subject: "",
        content: "",
        to_name: "",
        to_email: "",
        owner_id: "",
        property_id: "",
        violation_id: "",
        drc_submission_id: ""
      });
      fetchCorrespondence();
      fetchReferenceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create correspondence");
    }
  };

  const handleAIDraft = async () => {
    if (!composeForm.correspondence_type) {
      toast.error("Please select a correspondence type first");
      return;
    }

    // Capture current form state to prevent race conditions
    const currentType = composeForm.correspondence_type;
    const currentOwnerId = composeForm.owner_id;
    const currentPropertyId = composeForm.property_id;
    const currentViolationId = composeForm.violation_id;
    const currentDrcId = composeForm.drc_submission_id;

    setAiDrafting(true);
    try {
      const params = new URLSearchParams();
      params.append("correspondence_type", currentType);
      if (currentOwnerId && currentOwnerId !== "none") params.append("owner_id", currentOwnerId);
      if (currentPropertyId && currentPropertyId !== "none") params.append("property_id", currentPropertyId);
      if (currentViolationId && currentViolationId !== "none") params.append("violation_id", currentViolationId);
      if (currentDrcId && currentDrcId !== "none") params.append("drc_submission_id", currentDrcId);
      
      const response = await axios.post(`${API}/correspondence/draft-ai?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.draft) {
        // Only update if the correspondence type hasn't changed during the request
        setComposeForm(prev => {
          if (prev.correspondence_type !== currentType) {
            toast.info("Draft discarded - correspondence type was changed");
            return prev;
          }
          return {
            ...prev,
            content: response.data.draft,
            subject: prev.subject || `${CORRESPONDENCE_TYPES.find(t => t.value === currentType)?.label || 'Correspondence'}`
          };
        });
        toast.success("AI draft generated!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "AI drafting failed");
    } finally {
      setAiDrafting(false);
    }
  };

  const handleApprove = async (corrId) => {
    try {
      await axios.post(`${API}/correspondence/${corrId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Correspondence approved");
      fetchCorrespondence();
    } catch (error) {
      toast.error("Failed to approve");
    }
  };

  const handleSend = async (corrId) => {
    try {
      const response = await axios.post(`${API}/correspondence/${corrId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.mock) {
        toast.success("Email sent (mock mode - configure SendGrid for real delivery)");
      } else {
        toast.success("Email sent successfully!");
      }
      fetchCorrespondence();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send");
    }
  };

  const openView = (corr) => {
    setSelectedCorr(corr);
    setViewOpen(true);
  };

  const handlePromoteToComplaint = async () => {
    if (!promoteCorr) return;
    
    try {
      const payload = {
        target_type: promoteForm.target_type,
        owner_id: promoteForm.target_type === "owner" ? promoteForm.target_owner_id : null,
        property_id: promoteForm.target_property_id || null,
        role_name: promoteForm.target_type !== "owner" ? promoteForm.target_role : null
      };
      
      const response = await axios.post(
        `${API}/complaints/from-correspondence/${promoteCorr.correspondence_id}`,
        { submitted_against: [payload], category: promoteForm.category },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Correspondence promoted to complaint");
      setPromoteOpen(false);
      setPromoteCorr(null);
      setPromoteForm({
        target_type: "owner",
        target_owner_id: "",
        target_property_id: "",
        target_role: "",
        category: "other"
      });
      
      // Navigate to the new complaint
      navigate(`/admin/complaints`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to promote to complaint");
    }
  };

  const openPromote = (corr) => {
    setPromoteCorr(corr);
    setPromoteForm({
      target_type: "owner",
      target_owner_id: "",
      target_property_id: "",
      target_role: "",
      category: "neighbor_violation"
    });
    setPromoteOpen(true);
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, color: "bg-gray-500/10 text-gray-600" };
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const filteredCorrespondence = correspondence.filter(corr => {
    if (!searchQuery) return true;
    return (
      corr.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      corr.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      corr.to_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      corr.from_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const content = (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <div>
          <span className="font-serif text-lg font-semibold block">Correspondence Management</span>
          <span className="text-xs text-muted-foreground">Manage inbound and outbound communications</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mb-8">
        <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300" style={{ animationDelay: '0.1s' }}>
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-xs text-muted-foreground">Total</div>
          </CardContent>
        </Card>
        <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300" style={{ animationDelay: '0.15s' }}>
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-info">{stats.inbound}</div>
              <div className="text-xs text-muted-foreground">Inbound</div>
            </CardContent>
          </Card>
          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300" style={{ animationDelay: '0.2s' }}>
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-success">{stats.outbound}</div>
              <div className="text-xs text-muted-foreground">Outbound</div>
            </CardContent>
          </Card>
          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300" style={{ animationDelay: '0.25s' }}>
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-warning">{stats.pending_action}</div>
              <div className="text-xs text-muted-foreground">Pending</div>
            </CardContent>
          </Card>
          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300" style={{ animationDelay: '0.3s' }}>
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-primary">{stats.sent}</div>
              <div className="text-xs text-muted-foreground">Sent</div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-inbound-btn" className="hover:border-primary/50 transition-colors duration-200">
                <Inbox className="w-4 h-4 mr-2" />
                Upload Inbound
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif">Upload Inbound Correspondence</DialogTitle>
                <DialogDescription>
                  Upload a letter or document. AI will analyze and summarize the content.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Subject *</Label>
                  <Input
                    value={uploadForm.subject}
                    onChange={(e) => setUploadForm({ ...uploadForm, subject: e.target.value })}
                    placeholder="Brief subject line"
                    data-testid="upload-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select
                    value={uploadForm.correspondence_type}
                    onValueChange={(v) => setUploadForm({ ...uploadForm, correspondence_type: v })}
                  >
                    <SelectTrigger data-testid="upload-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {CORRESPONDENCE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Owner (Optional)</Label>
                    <Select
                      value={uploadForm.owner_id}
                      onValueChange={(v) => setUploadForm({ ...uploadForm, owner_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Link to owner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {owners.map((o) => (
                          <SelectItem key={o.owner_id} value={o.owner_id}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Property (Optional)</Label>
                    <Select
                      value={uploadForm.property_id}
                      onValueChange={(v) => setUploadForm({ ...uploadForm, property_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Link to property" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {properties.map((p) => (
                          <SelectItem key={p.property_id} value={p.property_id}>Lot {p.lot_number}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>File *</Label>
                  <Input
                    type="file"
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                    accept=".pdf,.txt,.doc,.docx,.jpg,.jpeg,.png"
                    data-testid="upload-file-input"
                  />
                  <p className="text-xs text-muted-foreground">PDF, TXT, DOC, or image files</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
                <Button onClick={handleUploadInbound} data-testid="upload-submit-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Analyze
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
            <DialogTrigger asChild>
              <Button data-testid="compose-outbound-btn" className="bg-primary text-primary-foreground">
                <SendHorizontal className="w-4 h-4 mr-2" />
                Compose Outbound
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif">Compose Outbound Correspondence</DialogTitle>
                <DialogDescription>
                  Create a new letter, notice, or response. Use AI to help draft the content.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type *</Label>
                    <Select
                      value={composeForm.correspondence_type}
                      onValueChange={(v) => setComposeForm({ ...composeForm, correspondence_type: v })}
                    >
                      <SelectTrigger data-testid="compose-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {CORRESPONDENCE_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Subject *</Label>
                    <Input
                      value={composeForm.subject}
                      onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                      placeholder="Email subject line"
                      data-testid="compose-subject-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>To Name</Label>
                    <Input
                      value={composeForm.to_name}
                      onChange={(e) => setComposeForm({ ...composeForm, to_name: e.target.value })}
                      placeholder="Recipient name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To Email</Label>
                    <Input
                      type="email"
                      value={composeForm.to_email}
                      onChange={(e) => setComposeForm({ ...composeForm, to_email: e.target.value })}
                      placeholder="recipient@example.com"
                      data-testid="compose-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Link to Owner</Label>
                    <Select
                      value={composeForm.owner_id}
                      onValueChange={(v) => {
                        const owner = owners.find(o => o.owner_id === v);
                        setComposeForm({ 
                          ...composeForm, 
                          owner_id: v,
                          to_name: owner?.name || composeForm.to_name,
                          to_email: owner?.email || composeForm.to_email
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {owners.map((o) => (
                          <SelectItem key={o.owner_id} value={o.owner_id}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Link to Property</Label>
                    <Select
                      value={composeForm.property_id}
                      onValueChange={(v) => setComposeForm({ ...composeForm, property_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {properties.map((p) => (
                          <SelectItem key={p.property_id} value={p.property_id}>Lot {p.lot_number}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Link to Notice</Label>
                    <Select
                      value={composeForm.violation_id}
                      onValueChange={(v) => setComposeForm({ ...composeForm, violation_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select violation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {violations.map((v) => (
                          <SelectItem key={v.violation_id} value={v.violation_id}>
                            {v.violation_type} - {v.description?.substring(0, 30)}...
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Link to DRC Submission</Label>
                    <Select
                      value={composeForm.drc_submission_id}
                      onValueChange={(v) => setComposeForm({ ...composeForm, drc_submission_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select submission" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {drcSubmissions.map((d) => (
                          <SelectItem key={d.submission_id} value={d.submission_id}>
                            {d.project_type} - {d.status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Content *</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAIDraft}
                      disabled={aiDrafting || !composeForm.correspondence_type}
                      data-testid="ai-draft-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {aiDrafting ? "Drafting..." : "AI Draft"}
                    </Button>
                  </div>
                  <Textarea
                    value={composeForm.content}
                    onChange={(e) => setComposeForm({ ...composeForm, content: e.target.value })}
                    placeholder="Write your message here or use AI Draft to generate content..."
                    rows={12}
                    className="font-mono text-sm"
                    data-testid="compose-content-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setComposeOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateOutbound} data-testid="compose-submit-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Save as Draft
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList>
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              <TabsTrigger value="inbound" data-testid="tab-inbound">
                <Inbox className="w-4 h-4 mr-1" />
                Inbound
              </TabsTrigger>
              <TabsTrigger value="outbound" data-testid="tab-outbound">
                <SendHorizontal className="w-4 h-4 mr-1" />
                Outbound
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-[200px]"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CORRESPONDENCE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Correspondence List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading correspondence...</div>
        ) : filteredCorrespondence.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No correspondence found</p>
              <div className="flex gap-2 justify-center mt-4">
                <Button variant="outline" onClick={() => setUploadOpen(true)}>
                  Upload Inbound
                </Button>
                <Button onClick={() => setComposeOpen(true)}>
                  Compose Outbound
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredCorrespondence.map((corr) => (
              <Card key={corr.correspondence_id} data-testid={`corr-${corr.correspondence_id}`} className="hover:shadow-floating transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {corr.direction === "inbound" ? (
                          <Inbox className="w-5 h-5 text-blue-600" />
                        ) : (
                          <SendHorizontal className="w-5 h-5 text-green-600" />
                        )}
                        <h3 className="font-semibold text-lg">{corr.subject}</h3>
                        {getStatusBadge(corr.status)}
                        <Badge variant="outline">
                          {CORRESPONDENCE_TYPES.find(t => t.value === corr.correspondence_type)?.label || corr.correspondence_type}
                        </Badge>
                      </div>
                      
                      {corr.direction === "inbound" && corr.summary && (
                        <div className="p-3 bg-primary/5 rounded-lg mb-3">
                          <p className="text-sm text-muted-foreground flex items-start gap-2">
                            <Sparkles className="w-4 h-4 mt-0.5 text-primary" />
                            <span><strong>AI Summary:</strong> {corr.summary}</span>
                          </p>
                        </div>
                      )}
                      
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                        {corr.content?.substring(0, 200)}...
                      </p>
                      
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                        {corr.direction === "outbound" && corr.to_email && (
                          <span>To: {corr.to_name || corr.to_email}</span>
                        )}
                        {corr.direction === "inbound" && corr.from_name && (
                          <span>From: {corr.from_name}</span>
                        )}
                        <span>{new Date(corr.created_at).toLocaleDateString()}</span>
                        {corr.sent_at && (
                          <span className="text-green-600">Sent: {new Date(corr.sent_at).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openView(corr)}
                        data-testid={`view-corr-${corr.correspondence_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      
                      {corr.direction === "inbound" && !corr.linked_complaint_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPromote(corr)}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          data-testid={`promote-corr-${corr.correspondence_id}`}
                        >
                          <Gavel className="w-4 h-4 mr-1" />
                          Promote to Complaint
                        </Button>
                      )}
                      
                      {corr.direction === "outbound" && corr.status === "draft" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleApprove(corr.correspondence_id)}
                          data-testid={`approve-corr-${corr.correspondence_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      )}
                      
                      {corr.direction === "outbound" && corr.status === "approved" && corr.to_email && (
                        <Button
                          size="sm"
                          onClick={() => handleSend(corr.correspondence_id)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`send-corr-${corr.correspondence_id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Send
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View Dialog */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">{selectedCorr?.subject}</DialogTitle>
              <DialogDescription>
                {selectedCorr?.direction === "inbound" ? "Inbound" : "Outbound"} • 
                {CORRESPONDENCE_TYPES.find(t => t.value === selectedCorr?.correspondence_type)?.label} • 
                {new Date(selectedCorr?.created_at).toLocaleString()}
              </DialogDescription>
            </DialogHeader>
            {selectedCorr && (
              <div className="space-y-4 py-4">
                <div className="flex gap-2">
                  {getStatusBadge(selectedCorr.status)}
                  {selectedCorr.approved_by && (
                    <Badge variant="outline">Approved</Badge>
                  )}
                </div>
                
                {selectedCorr.direction === "outbound" && (
                  <div className="grid grid-cols-2 gap-4 p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">To</p>
                      <p className="font-medium">{selectedCorr.to_name || "—"}</p>
                      <p className="text-sm text-muted-foreground">{selectedCorr.to_email || "No email"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Status</p>
                      <p className="font-medium">{STATUS_CONFIG[selectedCorr.status]?.label || selectedCorr.status}</p>
                      {selectedCorr.sent_at && (
                        <p className="text-sm text-muted-foreground">Sent: {new Date(selectedCorr.sent_at).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {selectedCorr.summary && (
                  <div className="p-3 bg-primary/5 rounded-lg">
                    <p className="text-sm font-medium mb-1 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      AI Summary
                    </p>
                    <p className="text-sm">{selectedCorr.summary}</p>
                  </div>
                )}
                
                {selectedCorr.relevant_rules?.length > 0 && (
                  <div className="p-3 bg-yellow-500/5 rounded-lg">
                    <p className="text-sm font-medium mb-1 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                      Relevant Rules Identified
                    </p>
                    <ul className="text-sm list-disc list-inside">
                      {selectedCorr.relevant_rules.map((rule, i) => (
                        <li key={i}>{rule}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedCorr.suggested_response && (
                  <div className="p-3 bg-green-500/5 rounded-lg">
                    <p className="text-sm font-medium mb-1">Suggested Response Approach</p>
                    <p className="text-sm">{selectedCorr.suggested_response}</p>
                  </div>
                )}
                
                <div className="border rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-2">Content</p>
                  <div className="whitespace-pre-wrap font-mono text-sm">
                    {selectedCorr.content}
                  </div>
                </div>
                
                {selectedCorr.attachments?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Attachments</p>
                    <div className="space-y-2">
                      {selectedCorr.attachments.map((att, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                          <FileText className="w-4 h-4" />
                          <span className="text-sm">{att.filename}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
              {selectedCorr?.direction === "outbound" && selectedCorr?.status === "draft" && (
                <Button onClick={() => { handleApprove(selectedCorr.correspondence_id); setViewOpen(false); }}>
                  Approve for Sending
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Promote to Complaint Dialog */}
        <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Gavel className="w-5 h-5 text-purple-600" />
                Promote to Formal Complaint
              </DialogTitle>
              <DialogDescription>
                Create a formal complaint from this correspondence. This will initiate the complaint workflow with AI analysis and investigation options.
              </DialogDescription>
            </DialogHeader>
            
            {promoteCorr && (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">{promoteCorr.subject}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    From: {promoteCorr.from_name || "Unknown"} • {new Date(promoteCorr.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                <div>
                  <Label>Complaint Category</Label>
                  <Select
                    value={promoteForm.category}
                    onValueChange={(v) => setPromoteForm({...promoteForm, category: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neighbor_violation">Neighbor Violation</SelectItem>
                      <SelectItem value="architectural">Architectural/DRC</SelectItem>
                      <SelectItem value="board_conduct">Board Conduct</SelectItem>
                      <SelectItem value="safety">Safety Concern</SelectItem>
                      <SelectItem value="nuisance">Nuisance</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="financial">Financial</SelectItem>
                      <SelectItem value="property_manager">Property Manager</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Complaint Against</Label>
                  <Select
                    value={promoteForm.target_type}
                    onValueChange={(v) => setPromoteForm({...promoteForm, target_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Specific Owner</SelectItem>
                      <SelectItem value="hoa_board">HOA Board</SelectItem>
                      <SelectItem value="drc">Design Review Committee</SelectItem>
                      <SelectItem value="property_manager">Property Manager</SelectItem>
                      <SelectItem value="general_hoa">General HOA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {promoteForm.target_type === "owner" && (
                  <div>
                    <Label>Target Owner (optional)</Label>
                    <Select
                      value={promoteForm.target_owner_id}
                      onValueChange={(v) => setPromoteForm({...promoteForm, target_owner_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent>
                        {owners.map(owner => (
                          <SelectItem key={owner.owner_id} value={owner.owner_id}>
                            {owner.first_name} {owner.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setPromoteOpen(false)}>Cancel</Button>
              <Button onClick={handlePromoteToComplaint} className="bg-purple-600 hover:bg-purple-700">
                <Gavel className="w-4 h-4 mr-2" />
                Create Complaint
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );

  if (embedded) return content;
  return <AdminSidebar>{content}</AdminSidebar>;
};

export default CorrespondencePage;
